import React,{useState,useEffect} from 'react';
import {useParams,useNavigate,Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import {useData} from '../contexts/DataContext';
import {useAuth} from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiArrowLeft,FiSave,FiFileText,FiTag,FiMail,FiCheck,FiSettings,FiTarget}=FiIcons;

const EditNote=()=> {
  const {noteId,memberId}=useParams();
  const navigate=useNavigate();
  const {notes,members,updateNote,getMemberGoals,getMemberMiniGoals}=useData();
  const {user}=useAuth();
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const [savedNote,setSavedNote]=useState(null);
  const [emailSending,setEmailSending]=useState(false);
  const [emailStatus,setEmailStatus]=useState('');
  const [emailEnabled,setEmailEnabled]=useState(false);
  const [checkingEmailConfig,setCheckingEmailConfig]=useState(true);
  const [formData,setFormData]=useState({
    title: '',
    content: '',
    category: 'General'
  });

  // Email options state
  const [emailOptions,setEmailOptions]=useState({
    includeStrategicGoals: false,
    includeMiniGoals: false
  });

  const categories=[
    'Performance',
    'Areas for Improvement',
    'Mindset',
    'Strategy',
    'Marketing',
    'Technical',
    'General'
  ];

  const selectedMember=members.find(m=> m.id.toString()===memberId);
  
  // Get member's goals for preview
  const memberStrategicGoals=selectedMember ? getMemberGoals(selectedMember.id).filter(g=> !g.is_archived) : [];
  const memberMiniGoals=selectedMember ? getMemberMiniGoals(selectedMember.id).filter(g=> !g.is_archived) : [];

  // Check email configuration when component mounts
  useEffect(()=> {
    const checkEmailConfiguration=async ()=> {
      setCheckingEmailConfig(true);
      try {
        const {isEmailEnabled}=await import('../utils/emailUtils');
        const enabled=await isEmailEnabled();
        setEmailEnabled(enabled);
      } catch (error) {
        console.error('Error checking email configuration:',error);
        setEmailEnabled(false);
      } finally {
        setCheckingEmailConfig(false);
      }
    };

    checkEmailConfiguration();
  },[]);

  // Load note data when component mounts
  useEffect(()=> {
    const note=notes.find(n=> n.id.toString()===noteId);
    if (note) {
      setFormData({
        title: note.title || '',
        content: note.content || '',
        category: note.category || 'General'
      });
    } else {
      setError('Session note not found');
    }
  },[noteId,notes]);

  const handleSubmit=async (e)=> {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const updatedNote=await updateNote({
        id: noteId,
        ...formData
      });
      setSavedNote(updatedNote);
      setEmailStatus('');
      // Don't navigate immediately - let user decide to email first
    } catch (err) {
      setError(err.message || 'Failed to update session note. Please try again.');
      console.error('Error updating note:',err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange=(e)=> {
    const {name,value}=e.target;
    setFormData(prev=> ({
      ...prev,
      [name]: value
    }));
  };

  const handleEmailOptionsChange=(e)=> {
    const {name,checked}=e.target;
    setEmailOptions(prev=> ({
      ...prev,
      [name]: checked
    }));
  };

  const handleEmailSessionNotes=async ()=> {
    if (!savedNote || !selectedMember) return;

    setEmailSending(true);
    setEmailStatus('');
    try {
      // Import email utility
      const {sendSessionSummaryEmail}=await import('../utils/emailUtils');
      
      // Prepare goal data based on user selections
      let goalData=null;
      if (emailOptions.includeStrategicGoals || emailOptions.includeMiniGoals) {
        goalData=[];
        
        if (emailOptions.includeStrategicGoals) {
          goalData=[...goalData,...memberStrategicGoals];
        }
        
        if (emailOptions.includeMiniGoals) {
          goalData=[...goalData,...memberMiniGoals];
        }
        
        // Sort goals by creation date (most recent first)
        goalData.sort((a,b)=> new Date(b.created_at) - new Date(a.created_at));
      }

      // Create session data with updated information
      const sessionData={
        ...savedNote,
        timestamp: new Date().toISOString(), // Use current timestamp for updated note
        coach_name: user?.name
      };

      // Send session summary email with optional goal data
      const result=await sendSessionSummaryEmail(selectedMember,sessionData,goalData);

      if (result.success) {
        setEmailStatus('success');
        // Navigate after successful email
        setTimeout(()=> {
          navigate(`/members/${memberId}`);
        },2000);
      } else {
        setEmailStatus('error');
        console.error('Email sending failed:',result.error);
      }
    } catch (error) {
      console.error('Error sending session email:',error);
      setEmailStatus('error');
    } finally {
      setEmailSending(false);
    }
  };

  const handleSkipEmail=()=> {
    navigate(`/members/${memberId}`);
  };

  // Check if current user is Peter for settings link
  const isPeter=user?.name?.toLowerCase().includes('peter') || user?.username?.toLowerCase().includes('peter');

  return (
    <div className="p-6">
      <motion.div
        initial={{opacity: 0,y: 20}}
        animate={{opacity: 1,y: 0}}
        className="mb-8"
      >
        <div className="flex items-center space-x-4 mb-4">
          <Link
            to={`/members/${memberId}`}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            <span>Back to Member</span>
          </Link>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Edit Session Notes</h1>
        <p className="text-gray-600">Update session information</p>
      </motion.div>

      <motion.div
        initial={{opacity: 0,y: 20}}
        animate={{opacity: 1,y: 0}}
        transition={{delay: 0.1}}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      >
        {/* Success State - Show after note is updated */}
        {savedNote ? (
          <div className="space-y-6">
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiCheck} className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Session Notes Updated Successfully!</h3>
              <p className="text-gray-600 mb-6">
                Your updated session notes for <strong>{selectedMember?.name}</strong> have been saved.
              </p>

              {/* Email Status Messages */}
              {emailStatus==='success' && (
                <motion.div
                  initial={{opacity: 0,y: -10}}
                  animate={{opacity: 1,y: 0}}
                  className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-center"
                >
                  <SafeIcon icon={FiCheck} className="w-5 h-5 mr-2" />
                  Updated session notes emailed successfully! Redirecting...
                </motion.div>
              )}

              {emailStatus==='error' && (
                <motion.div
                  initial={{opacity: 0,y: -10}}
                  animate={{opacity: 1,y: 0}}
                  className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
                >
                  Failed to send email. You can try again later from the member's profile.
                </motion.div>
              )}

              {/* Email Configuration Check */}
              {checkingEmailConfig ? (
                <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  Checking email configuration...
                </div>
              ) : !emailEnabled ? (
                <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-lg">
                  <div className="flex items-center mb-2">
                    <SafeIcon icon={FiSettings} className="w-5 h-5 mr-2" />
                    <span className="font-medium">Email functionality is not configured</span>
                  </div>
                  <p className="text-sm">
                    Email sending is disabled. {isPeter ? 'Configure email settings to enable this feature.' : 'Contact your administrator to enable email functionality.'}
                  </p>
                  {isPeter && (
                    <Link
                      to="/settings"
                      className="inline-flex items-center mt-2 text-amber-800 hover:text-amber-900 font-medium text-sm"
                    >
                      <SafeIcon icon={FiSettings} className="w-4 h-4 mr-1" />
                      Go to Settings
                    </Link>
                  )}
                </div>
              ) : null}

              {/* Email Options - Only show if email is enabled */}
              {emailEnabled && (
                <div className="mb-6 text-left bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Email Options:</h4>
                  
                  {/* Strategic Goals Option */}
                  {memberStrategicGoals.length > 0 && (
                    <div className="mb-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="includeStrategicGoals"
                          checked={emailOptions.includeStrategicGoals}
                          onChange={handleEmailOptionsChange}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 flex items-center">
                          <SafeIcon icon={FiTarget} className="w-4 h-4 mr-1 text-primary-600" />
                          Include Strategic Goals ({memberStrategicGoals.length} active)
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Mini-Goals Option */}
                  {memberMiniGoals.length > 0 && (
                    <div className="mb-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="includeMiniGoals"
                          checked={emailOptions.includeMiniGoals}
                          onChange={handleEmailOptionsChange}
                          className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 flex items-center">
                          <SafeIcon icon={FiTarget} className="w-4 h-4 mr-1 text-emerald-600" />
                          Include Mini-Goals ({memberMiniGoals.length} active)
                        </span>
                      </label>
                    </div>
                  )}

                  {/* No Goals Message */}
                  {memberStrategicGoals.length === 0 && memberMiniGoals.length === 0 && (
                    <p className="text-sm text-gray-500 italic">
                      No active goals to include. Goals can be added from the member's profile.
                    </p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {emailEnabled && (
                  <button
                    onClick={handleEmailSessionNotes}
                    disabled={emailSending || emailStatus==='success'}
                    className="flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {emailSending ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Sending Updated Notes...</span>
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiMail} className="w-4 h-4" />
                        <span>Email Updated Notes to Member</span>
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={handleSkipEmail}
                  disabled={emailSending}
                  className="px-6 py-3 text-gray-700 hover:text-gray-900 font-medium transition-colors disabled:opacity-50"
                >
                  {emailEnabled ? 'Skip & Continue' : 'Continue'}
                </button>
              </div>

              {/* Email Preview - Only show if email is enabled */}
              {emailEnabled && (
                <div className="mt-8 text-left bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Email Preview:</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>To:</strong> {selectedMember?.email}</p>
                    <p><strong>Subject:</strong> Session Summary: {savedNote.title}</p>
                    <p><strong>Content:</strong> Updated session notes, key takeaways, and next steps</p>
                    {(emailOptions.includeStrategicGoals || emailOptions.includeMiniGoals) && (
                      <p><strong>Additional:</strong> 
                        {emailOptions.includeStrategicGoals && ` Strategic Goals (${memberStrategicGoals.length})`}
                        {emailOptions.includeStrategicGoals && emailOptions.includeMiniGoals && ','}
                        {emailOptions.includeMiniGoals && ` Mini-Goals (${memberMiniGoals.length})`}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Form State - Show before note is updated */
          <>
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiTag} className="w-4 h-4 inline mr-2" />
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  {categories.map(category=> (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiFileText} className="w-4 h-4 inline mr-2" />
                  Session Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter a title for this session"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Session Notes
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  rows={12}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y font-mono text-sm leading-relaxed"
                  placeholder="Write detailed notes about this session...

You can format your notes with:
- Line breaks for new paragraphs
- Multiple spaces for indentation  
- Bullet points and lists
- All formatting will be preserved when saved"
                  required
                  style={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace'
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Tip: Line breaks and spacing will be preserved exactly as you type them
                </p>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <SafeIcon icon={FiSave} className="w-4 h-4" />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default EditNote;